// Segundo paso del login (MFA por TOTP, TrackConstruccion script 006).
// Va DENTRO de la tarjeta del login (sin modal): 'verificar' pide el código de la app
// (o uno de recuperación); 'enrolar' configura la app cuando el perfil exige MFA y el
// usuario aún no lo tiene, y al final muestra los códigos de recuperación UNA sola vez.
import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { QRCodeSVG } from 'qrcode.react';
import { Copy, Download, Loader2, ShieldCheck } from 'lucide-react';
import { authAPI, type MfaDesafio, type MfaRespuesta } from '@/lib/api/auth.api';
import { getErrorMessage } from '@/lib/api/client';
import { completarSesion } from '@/lib/hooks/useAuth';
import { useTranslation } from '@/lib/hooks/useTranslation';

interface Props {
  desafio: MfaDesafio;
  acc: string;              // color de acento del tema del login
  onListo: () => void;      // sesión iniciada
  onCancelar: () => void;   // volver al formulario de usuario/clave
  tono?: 'oscuro' | 'claro'; // oscuro = escena HUD de K2E; claro = panel blanco de TrackConstruction
}

// Clases por tono (el resto del componente es idéntico)
const TONOS = {
  oscuro: {
    base: 'text-slate-300', titulo: 'text-[#eaf4fb]', valor: 'text-[#e9f4fb]', tenue: 'text-slate-400',
    caja: 'border-white/10 bg-black/30', boton: 'border-white/15 hover:bg-white/5',
    input: 'text-[#e9f4fb] placeholder:text-white/25', btnTexto: 'text-[#04121a]', error: 'text-red-300',
    h2: 'text-[13px] font-semibold uppercase tracking-[0.2em]', etiqueta: 'text-[9px] uppercase tracking-[0.28em]', btnTam: 'py-3 text-[11px] uppercase tracking-[0.28em]', ayuda: 'text-xs',
  },
  claro: {
    base: 'text-slate-600', titulo: 'text-slate-900', valor: 'text-slate-900', tenue: 'text-slate-500',
    caja: 'border-slate-200 bg-slate-50', boton: 'border-slate-300 hover:bg-slate-50',
    input: 'text-slate-900 placeholder:text-slate-300', btnTexto: 'text-white', error: 'text-red-700',
    h2: 'font-display text-[30px] font-bold leading-tight', etiqueta: 'text-sm font-semibold', btnTam: 'h-14 text-base', ayuda: 'text-[15px]',
  },
};

export const agruparSecreto = (s: string) => s.replace(/(.{4})/g, '$1 ').trim();

export function descargarCodigos(codigos: string[], usuario?: string) {
  const txt = `${usuario ? usuario + ' — ' : ''}Recovery codes / Códigos de recuperación\n${new Date().toLocaleString()}\n\n${codigos.join('\n')}\n`;
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([txt], { type: 'text/plain' }));
  a.download = 'recovery-codes.txt';
  a.click();
  URL.revokeObjectURL(a.href);
}

export function MfaPaso({ desafio, acc, onListo, onCancelar, tono = 'oscuro' }: Props) {
  const k = TONOS[tono];
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [codigo, setCodigo] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState('');
  const [alta, setAlta] = useState<{ secreto: string; uri: string } | null>(null);
  const [final, setFinal] = useState<MfaRespuesta | null>(null);   // enrolado: mostrar códigos antes de entrar

  useEffect(() => {
    if (desafio.mfa !== 'enrolar') return;
    authAPI.mfaEnrolarIniciar(desafio.mfaTicket)
      .then((r) => (r.secreto && r.uri ? setAlta({ secreto: r.secreto, uri: r.uri }) : setError(t('mfa.vencido'))))
      .catch((e) => setError(getErrorMessage(e)));
  }, [desafio, t]);

  const entrar = (r: MfaRespuesta) => { completarSesion(qc, r.user); onListo(); };

  const enviar = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!codigo.trim()) return;
    setEnviando(true); setError('');
    try {
      const r = desafio.mfa === 'enrolar'
        ? await authAPI.mfaEnrolarConfirmar(desafio.mfaTicket, codigo)
        : await authAPI.loginMfa(desafio.mfaTicket, codigo);
      if (r.token) {
        if (r.codigosRecuperacion?.length) setFinal(r); else entrar(r);
      } else if (r.error === 'mfa_codigo_invalido') {
        setError(t('mfa.invalido', { n: r.intentosRestantes ?? 0 })); setCodigo('');
      } else {
        setError(r.error === 'mfa_bloqueado' ? t('mfa.bloqueado') : t('mfa.vencido'));
      }
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setEnviando(false);
    }
  };

  const terminal = error && (error === t('mfa.bloqueado') || error === t('mfa.vencido'));
  const inputCls = tono === 'claro'
    ? `h-14 w-full rounded-md border border-slate-300 bg-white px-3 text-center text-2xl tracking-[0.5em] ${k.input} focus:border-[#1F4E8C] focus:outline-none focus:ring-2 focus:ring-[#C2410C]/60`
    : `w-full border-0 border-b bg-transparent px-0 py-1.5 text-center text-lg tracking-[0.4em] ${k.input} focus:outline-none focus:ring-0`;
  const btnCls = `flex w-full items-center justify-center gap-2 rounded-sm font-semibold ${k.btnTam} ${k.btnTexto} transition hover:brightness-110 disabled:opacity-70`;

  if (final?.codigosRecuperacion) {
    const cods = final.codigosRecuperacion;
    return (
      <div className={`text-sm ${k.base}`}>
        <h2 className={`mb-2 flex items-center gap-2 ${tono === 'claro' ? '' : 'justify-center text-center'} ${k.h2} ${k.titulo}`}>
          <ShieldCheck className="h-4 w-4" style={{ color: acc }} /> {t('mfa.codigosTitulo')}
        </h2>
        <p className="mb-3 text-xs">{t('mfa.codigosAyuda')}</p>
        <div className={`mb-3 grid grid-cols-2 gap-x-4 gap-y-1 rounded border p-3 font-mono text-[13px] ${k.caja} ${k.valor}`}>
          {cods.map((c) => <span key={c}>{c}</span>)}
        </div>
        <div className="mb-4 flex gap-2">
          <button type="button" className={`flex flex-1 items-center justify-center gap-1.5 rounded-sm border py-2 text-xs ${k.boton}`}
            onClick={() => void navigator.clipboard?.writeText(cods.join('\n'))}>
            <Copy className="h-3.5 w-3.5" /> {t('mfa.copiar')}
          </button>
          <button type="button" className={`flex flex-1 items-center justify-center gap-1.5 rounded-sm border py-2 text-xs ${k.boton}`}
            onClick={() => descargarCodigos(cods, final.user?.username)}>
            <Download className="h-3.5 w-3.5" /> {t('mfa.descargar')}
          </button>
        </div>
        <button type="button" className={btnCls} style={{ background: acc }} onClick={() => entrar(final)}>
          {t('mfa.guardadosContinuar')}
        </button>
      </div>
    );
  }

  return (
    <div className={`text-sm ${k.base}`}>
      <h2 className={`mb-2 flex items-center gap-2 ${tono === 'claro' ? '' : 'justify-center text-center'} ${k.h2} ${k.titulo}`}>
        <ShieldCheck className="h-4 w-4" style={{ color: acc }} />
        {desafio.mfa === 'enrolar' ? t('mfa.enrolarTitulo') : t('mfa.verificarTitulo')}
      </h2>

      {desafio.mfa === 'enrolar' ? (
        <>
          <p className={`mb-3 ${k.ayuda}`}>{t('mfa.enrolarAyuda')}</p>
          {alta ? (
            <div className="mb-4 flex flex-col items-center gap-2">
              <div className="rounded bg-white p-2"><QRCodeSVG value={alta.uri} size={148} /></div>
              <p className={`text-[10px] uppercase tracking-[0.15em] ${k.tenue}`}>{t('mfa.claveManual')}</p>
              <code className={`select-all break-all text-center text-[12px] ${k.valor}`}>{agruparSecreto(alta.secreto)}</code>
            </div>
          ) : !error && <div className="mb-4 flex justify-center"><Loader2 className="h-5 w-5 animate-spin" /></div>}
        </>
      ) : (
        <p className={`mb-4 ${k.ayuda}`}>{t('mfa.verificarAyuda')}</p>
      )}

      {!terminal && (desafio.mfa === 'verificar' || alta) && (
        <form onSubmit={enviar}>
          <label htmlFor="mfa-codigo" className={`mb-1 block ${k.etiqueta}`} style={{ color: tono === 'claro' ? undefined : acc }}>{t('mfa.codigo')}</label>
          <input id="mfa-codigo" value={codigo} onChange={(e) => setCodigo(e.target.value)} autoFocus
            inputMode={desafio.mfa === 'enrolar' ? 'numeric' : 'text'} autoComplete="one-time-code" maxLength={11}
            placeholder="000000" className={inputCls} style={{ borderColor: acc }} disabled={enviando} />
          {desafio.mfa === 'verificar' && <p className={`mt-1.5 text-[11px] ${k.tenue}`}>{t('mfa.recuperacionAyuda')}</p>}
          {error && <p className={`mt-3 rounded border border-red-500/40 bg-red-500/10 p-2 text-xs ${k.error}`}>{error}</p>}
          <button type="submit" disabled={enviando || !codigo.trim()} className={`${btnCls} mt-4`} style={{ background: acc }}>
            {enviando && <Loader2 className="h-4 w-4 animate-spin" />}
            {t('mfa.verificar')}
          </button>
        </form>
      )}
      {terminal && <p className={`rounded border border-red-500/40 bg-red-500/10 p-2 text-xs ${k.error}`}>{error}</p>}

      <button type="button" onClick={onCancelar} className={`mt-4 w-full text-center text-[11px] uppercase tracking-[0.12em] ${k.tenue} hover:underline`}>
        {t('mfa.volver')}
      </button>
    </div>
  );
}
